/**
 * SINGLE precedence source for the top-nav identity avatars — the profile
 * TRIGGER, the profile DROPDOWN header, and the impersonate-picker
 * "Currently signed in as" row.
 *
 * Regression guards (docs/REGRESSIONS.md 2026-07-01):
 *
 * (a) STALE CACHE AT A CONSTANT URL — the trigger's by-email source
 *     (`/api/crm/team/<email>/photo?noFallback=1&v=2`) NEVER changed per
 *     upload while the server cached it (photo OR the no-photo 404) for 1h,
 *     so a freshly uploaded photo kept rendering as the OLD photo or as
 *     INITIALS until the cache expired. Every per-upload-versioned source
 *     must therefore win over the constant-URL path, and the by-email path
 *     itself gets the version appended (`&ver=`) whenever one is known.
 *
 * (b) NAV TRIGGER SOURCE GAP (impersonation) — the impersonating branch only
 *     knew `hrProfile.photoUrl`, which loads when Preferences > Profile
 *     mounts; on every other route it fell back to the stale by-email path.
 *     `/api/me` now carries a VERSIONED `photoUrl`/`photoVersion` for the
 *     EFFECTIVE user (impersonation-aware — the middleware swaps identity),
 *     so the trigger has a fresh source on EVERY route.
 *
 * Precedence (first hit wins):
 *   1. `hrProfilePhotoUrl` — the loaded HR profile of the EFFECTIVE user
 *      (Preferences Profile card source; updated in-memory on upload, so it
 *      is post-upload instant). Applies to self AND impersonation.
 *   2. `mePhotoUrl` — the versioned photo URL from `/api/me` (works on every
 *      route, self AND impersonating; version changes per upload).
 *   3. `prefsPhoto` — the signed-in user's own cached avatar. SELF ONLY:
 *      while impersonating this is the ADMIN's photo and must NEVER render
 *      as the impersonated user (display-only invariant — impersonation
 *      never reads or writes the admin's prefsPhoto/localStorage).
 *   4. by-email `<UserPhotoAvatar>` (`/api/crm/team/<email>/photo`) with the
 *      known `version` appended so even this path busts per upload.
 *   5. The caller's colored-initials fallback (inside UserPhotoAvatar).
 *
 * Supersedes `impersonatedNavAvatarPhoto` (src/lib/profileImpersonationEdit.ts)
 * — its impersonation invariants are re-encoded here so there is ONE
 * precedence source. This module is pure (no React, no fetch).
 */

/** Marker literals — rendered into `data-avatar-source` on the live <img> so
 *  the winning source is greppable in the DOM and in the deployed bundle. */
export const AVATAR_SOURCE_TAGS = {
  hrProfile: 'hrprofile-avatar',
  meVersioned: 'me-versioned-avatar',
  prefs: 'prefs-avatar',
} as const

export type NavAvatarSource =
  /** Render `src` directly in an <img data-avatar-source={source}> */
  | { kind: 'img'; src: string; source: (typeof AVATAR_SOURCE_TAGS)[keyof typeof AVATAR_SOURCE_TAGS] }
  /** Fall back to the by-email <UserPhotoAvatar> (then initials); `version`
   *  (when known) is appended to its URL as `&ver=` for per-upload busting. */
  | { kind: 'by-email'; version: string | null }

export function navAvatarSource(opts: {
  /** true when an admin is acting as another user (impersonation active) */
  impersonating: boolean
  /** loaded HR profile photo URL of the EFFECTIVE user (self or impersonated) */
  hrProfilePhotoUrl?: string | null
  /** versioned photo URL from /api/me (effective user; impersonation-aware) */
  mePhotoUrl?: string | null
  /** per-upload photo version from /api/me (opaque cache-bust token) */
  mePhotoVersion?: string | null
  /** the signed-in user's OWN cached avatar — never shown while impersonating */
  prefsPhoto?: string | null
}): NavAvatarSource {
  if (opts.hrProfilePhotoUrl) {
    return { kind: 'img', src: opts.hrProfilePhotoUrl, source: AVATAR_SOURCE_TAGS.hrProfile }
  }
  if (opts.mePhotoUrl) {
    return { kind: 'img', src: opts.mePhotoUrl, source: AVATAR_SOURCE_TAGS.meVersioned }
  }
  // prefsPhoto is the ADMIN's own avatar — display-only rule: never render it
  // while impersonating (and impersonation flows never write into it either).
  if (!opts.impersonating && opts.prefsPhoto) {
    return { kind: 'img', src: opts.prefsPhoto, source: AVATAR_SOURCE_TAGS.prefs }
  }
  return { kind: 'by-email', version: opts.mePhotoVersion || null }
}

/**
 * By-email team photo URL used by <UserPhotoAvatar>. `noFallback=1` keeps the
 * 404-on-miss contract (callers render their own initials); `v=2` busts
 * browser-cached 404s from the Graph-only era; `ver=` (when known) makes the
 * URL unique PER UPLOAD so no constant-URL cache can pin a pre-upload
 * response (the server also sends `Cache-Control: no-store` on noFallback
 * 404s and an ETag on hits — this is the client half of that contract).
 */
export function teamPhotoSrc(email: string, version?: string | null): string {
  const base = `/api/crm/team/${encodeURIComponent(email)}/photo?noFallback=1&v=2`
  return version ? `${base}&ver=${encodeURIComponent(version)}` : base
}

/**
 * Per-upload cache-bust version derived from an HR photo R2 key
 * (`hr-profiles/<id>/photo-<ts>.<ext>` → `<ts>`). Mirrors the server-side
 * derivation in /api/me. Null when the key doesn't carry one — callers fall
 * back to a fresh token (e.g. `String(Date.now())`).
 */
export function photoVersionFromKey(key: string | null | undefined): string | null {
  if (!key) return null
  const m = String(key).match(/photo-(\d+)/)
  return m ? m[1] : null
}
